import { useQuery } from '@tanstack/react-query'

import { apiClient } from '../../apiClient'

type ProgramQualityMetricsResponse = {
  f1Score: number
  iou: number
  accuracy: number
  precision: number
  recall: number
  audited: number
}
type ProgramQualityMetricsSummaryResponse = {
  class: string
  truePositiveCount: number
  trueNegativeCount: number
  falseNegativeCount: number
  falsePositiveCount: number
}

type ProgramQualityResponseDailyBreakdown = {
  date: string
  accuracy: number
  withinTargetCount: number
  missedTargetCount: number
}

type ProgramQualityResponse = {
  total: ProgramQualityMetricsResponse
  trend: ProgramQualityMetricsResponse
  summary: ProgramQualityMetricsSummaryResponse[]
  daily: ProgramQualityResponseDailyBreakdown[]
  programConfig: {
    targetAccuracy: number
  }
}

type DatePeriod = {
  start: string
  end: string
}

type ConfusionMatrixValues = {
  truePositive: number
  falsePositive: number
  falseNegative: number
  trueNegative: number
}

export const PROGRAM_QUALITY_METRICS_KEY = 'program-quality-metrics'

export function useGetById(
  period: DatePeriod | null,
  granularity: 'daily' | 'weekly' | 'monthly' = 'monthly',
  programId?: string
) {
  return useQuery({
    queryKey: [PROGRAM_QUALITY_METRICS_KEY, period?.start, period?.end, programId, granularity],
    queryFn: async () => {
      const { data } = await apiClient.get<ProgramQualityResponse>(
        `/int/metrics/quality/programs/${programId}`,
        {
          params: {
            granularity,
            start_date: period!.start,
            end_date: period!.end,
          },
        }
      )

      return data
    },
    select: (data) => {
      const classes = ['All annotations', ...data.summary.map((values) => values.class)]
      const values = data.summary.reduce(
        (data, summary) => {
          return {
            ...data,
            [summary.class]: {
              truePositive: summary.truePositiveCount,
              falsePositive: summary.falsePositiveCount,
              falseNegative: summary.falseNegativeCount,
              trueNegative: summary.trueNegativeCount,
            },
            ['All annotations']: {
              truePositive: data['All annotations']?.truePositive + summary.truePositiveCount,
              falsePositive: data['All annotations']?.falsePositive + summary.falsePositiveCount,
              falseNegative: data['All annotations']?.falseNegative + summary.falseNegativeCount,
              trueNegative: data['All annotations']?.trueNegative + summary.trueNegativeCount,
            },
          }
        },
        {
          ['All annotations']: {
            truePositive: 0,
            falsePositive: 0,
            falseNegative: 0,
            trueNegative: 0,
          },
        } as Record<string, ConfusionMatrixValues>
      )

      return { classes, values }
    },
    enabled: Boolean(programId) && Boolean(period),
    refetchOnWindowFocus: false,
  })
}
